import React, { useLayoutEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Checkbox from 'components/shared/Checkbox/Checkbox';
import Hr from 'components/shared/Hr/Hr';

/**
 * AcknowledgementFooter - A shared acknowledgement checkbox component that positions itself
 * sticky at the bottom when content is scrollable, or inline when not scrollable.
 *
 * @param {Object} props
 * @param {string|React.ReactNode} props.text - Checkbox label text (can be string or JSX)
 * @param {boolean} props.isChecked - Whether checkbox is checked
 * @param {Function} props.onChange - Checkbox change handler
 * @param {boolean} props.isDisabled - Whether checkbox is disabled
 * @param {React.RefObject} props.scrollContainerRef - Ref to the scrollable container element
 * @param {React.ReactNode} [props.banner] - Optional banner to display below the checkbox
 * @param {string} [props.className] - Additional className for wrapper
 */
const AcknowledgementFooter = ({
  text,
  isChecked,
  onChange,
  isDisabled,
  scrollContainerRef,
  banner,
  className
}) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const acknowledgementRef = useRef(null);
  const isScrollableRef = useRef(false);

  useLayoutEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef?.current && acknowledgementRef?.current) {
        const container = scrollContainerRef.current;
        const footer = acknowledgementRef.current;
        const footerHeight = footer.offsetHeight;

        const contentHeightWithoutFooter =
          container.scrollHeight - (isScrollableRef.current ? 0 : footerHeight);
        const availableHeight = container.clientHeight - footerHeight;

        const shouldBeScrollable = contentHeightWithoutFooter > availableHeight + 5;

        if (shouldBeScrollable !== isScrollableRef.current) {
          if (isScrollableRef.current && !shouldBeScrollable) {
            if (contentHeightWithoutFooter < availableHeight - 20) {
              isScrollableRef.current = false;
              setIsScrollable(false);
            }
          } else {
            isScrollableRef.current = shouldBeScrollable;
            setIsScrollable(shouldBeScrollable);
          }
        }
      }
    };

    const timeoutId = setTimeout(checkScrollable, 50);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkScrollable);
    });

    if (scrollContainerRef?.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    window.addEventListener('resize', checkScrollable);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollable);
    };
  }, [scrollContainerRef]);

  const checkboxContent = (
    <div className="flex items-start gap-[7px]">
      <div className="pt-0.5">
        <Checkbox disabled={isDisabled} isChecked={isChecked} size={18} onChange={onChange} />
      </div>
      <div className="text-sm">{typeof text === 'string' ? <div>{text}</div> : text}</div>
    </div>
  );

  if (isScrollable) {
    return (
      <div
        ref={acknowledgementRef}
        className={cn(
          'sticky bottom-0 z-10 w-full border border-b-0 border-l-0 border-r-0 border-solid border-t-neutral-200 pb-4 pt-3 [@media(max-width:1200px)]:w-full [@media(max-width:1200px)]:py-0',
          className
        )}>
        {checkboxContent}
        {banner && (
          <div className="-mx-8 sm:!-mx-4 [@media(max-width:1200px)]:-mx-12">{banner}</div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={acknowledgementRef}
      className={cn('mt-4 [@media(max-width:1200px)]:w-full', className)}>
      <Hr className="my-4" />
      {checkboxContent}
      {banner && <div className="-mx-8 sm:!-mx-4 [@media(max-width:1200px)]:-mx-12">{banner}</div>}
    </div>
  );
};

export default AcknowledgementFooter;
